import { Link } from "@tanstack/react-router";
import { Moon, Sun, Sunrise, ListOrdered } from "lucide-react";
import type { Location } from "@/data/locations";

type Variant = "sholat" | "imsak" | "buka-puasa";

function pathFor(variant: Variant, loc: Location): string {
  const seg = variant === "buka-puasa" ? "jadwal-buka-puasa" : `jadwal-${variant}`;
  if (loc.kind === "kecamatan") return `/${seg}/${loc.parentSlug}/${loc.slug}/`;
  return `/${seg}/${loc.slug}/`;
}

const META: Record<Variant, { label: string; icon: typeof Moon; color: string }> = {
  sholat: { label: "Jadwal Sholat", icon: Moon, color: "text-prayer" },
  imsak: { label: "Jadwal Imsakiyah", icon: Sunrise, color: "text-prayer" },
  "buka-puasa": { label: "Jadwal Buka Puasa", icon: Sun, color: "text-prayer" },
};

/**
 * Three-way cross navigation linking sholat ↔ imsak ↔ buka-puasa for the same
 * location. Highlights the active variant and points the others to the equivalent
 * URL so users (and crawlers) get strong reciprocal internal links.
 */
export function PrayerCrossNav({ active, location }: { active: Variant; location: Location }) {
  const variants: Variant[] = ["sholat", "imsak", "buka-puasa"];
  return (
    <nav
      aria-label="Navigasi jadwal terkait"
      className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-2 text-sm"
    >
      {variants.map((v) => {
        const meta = META[v];
        const Icon = meta.icon;
        const isActive = v === active;
        return isActive ? (
          <span
            key={v}
            aria-current="page"
            className="inline-flex items-center gap-1.5 rounded-md bg-prayer px-3 py-1.5 font-medium text-prayer-foreground"
          >
            <Icon className="h-3.5 w-3.5" /> {meta.label}
          </span>
        ) : (
          <Link
            key={v}
            to={pathFor(v, location) as never}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 font-medium text-foreground hover:border-prayer hover:bg-prayer/10 hover:text-prayer"
          >
            <Icon className="h-3.5 w-3.5" /> {meta.label}
          </Link>
        );
      })}
      <Link
        to="/jadwal-sholat/lokasi/"
        className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-foreground hover:bg-accent"
      >
        <ListOrdered className="h-3.5 w-3.5" /> Indeks lokasi
      </Link>
    </nav>
  );
}